import React, { memo, useCallback, useEffect, useState, useRef } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { SelectionOverlay } from "./SelectionOverlay";
import { getAdaptiveFontSize, normalizeBoothShape, SCALE_FACTOR } from "../utils";
import { useFloorPlanner } from "../../../store/useFloorPlanner";

// Triangular arrow component - defined outside to prevent re-creation on each render
const TriangleArrow = memo(({ direction, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isClickingRef = useRef(false);
  // Triangle size
  const size = 16;
  // Light gray when not hovered, darker gray when hovered
  const color = isHovered ? "#6b7280" : "#e3e3e3"; // gray-500 on hover, slightly lighter default
  // Position and rotation based on direction
  const getStyles = () => {
    const base = {
      position: "absolute",
      width: 0,
      height: 0,
      cursor: "pointer",
      zIndex: 9998, // Just below handles
      pointerEvents: "auto",
      transition: "border-color 0.2s ease",
    };
    switch (direction) {
      case "top":
        return {
          ...base,
          left: "50%",
          top: "-25px",
          marginLeft: -size / 2,
          borderLeft: `${size / 2}px solid transparent`,
          borderRight: `${size / 2}px solid transparent`,
          borderBottom: `${size}px solid ${color}`,
        };
      case "right":
        return {
          ...base,
          right: "-25px",
          top: "50%",
          marginTop: -size / 2,
          borderTop: `${size / 2}px solid transparent`,
          borderBottom: `${size / 2}px solid transparent`,
          borderLeft: `${size}px solid ${color}`,
        };
      case "bottom":
        return {
          ...base,
          left: "50%",
          bottom: "-25px",
          marginLeft: -size / 2,
          borderLeft: `${size / 2}px solid transparent`,
          borderRight: `${size / 2}px solid transparent`,
          borderTop: `${size}px solid ${color}`,
        };
      case "left":
        return {
          ...base,
          left: "-25px",
          top: "50%",
          marginTop: -size / 2,
          borderTop: `${size / 2}px solid transparent`,
          borderBottom: `${size / 2}px solid transparent`,
          borderRight: `${size}px solid ${color}`,
        };
    }
  };
  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      // Prevent multiple clicks
      if (isClickingRef.current) return;
      isClickingRef.current = true;
      onClick();
      // Reset after a short delay
      setTimeout(() => {
        isClickingRef.current = false;
      }, 300);
    },
    [onClick],
  );
  return (
    <div
      className="nodrag add-booth-arrow"
      style={getStyles()}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={`Add booth ${direction === "top" ? "above" : direction === "bottom" ? "below" : `to the ${direction}`}`}
    />
  );
});

const subtractInterval = (source, cutters) => {
  let segments = [source];

  cutters.forEach((cutter) => {
    const nextSegments = [];

    segments.forEach((segment) => {
      const start = Math.max(segment.start, cutter.start);
      const end = Math.min(segment.end, cutter.end);

      if (start >= end) {
        nextSegments.push(segment);
        return;
      }

      if (segment.start < start) {
        nextSegments.push({ start: segment.start, end: start });
      }

      if (end < segment.end) {
        nextSegments.push({ start: end, end: segment.end });
      }
    });

    segments = nextSegments;
  });

  return segments;
};

const getExternalBorderSegments = (parts) => {
  const edgeSegments = [];

  parts.forEach((part) => {
    const left = part.x;
    const right = part.x + part.width;
    const top = part.y;
    const bottom = part.y + part.height;

    const topCutters = parts
      .filter((other) => other !== part && other.y + other.height === top)
      .map((other) => ({
        start: Math.max(left, other.x),
        end: Math.min(right, other.x + other.width),
      }))
      .filter((segment) => segment.start < segment.end);
    subtractInterval({ start: left, end: right }, topCutters).forEach((segment) => {
      edgeSegments.push({ orientation: "horizontal", x: segment.start, y: top, length: segment.end - segment.start });
    });

    const bottomCutters = parts
      .filter((other) => other !== part && other.y === bottom)
      .map((other) => ({
        start: Math.max(left, other.x),
        end: Math.min(right, other.x + other.width),
      }))
      .filter((segment) => segment.start < segment.end);
    subtractInterval({ start: left, end: right }, bottomCutters).forEach((segment) => {
      edgeSegments.push({ orientation: "horizontal", x: segment.start, y: bottom, length: segment.end - segment.start });
    });

    const leftCutters = parts
      .filter((other) => other !== part && other.x + other.width === left)
      .map((other) => ({
        start: Math.max(top, other.y),
        end: Math.min(bottom, other.y + other.height),
      }))
      .filter((segment) => segment.start < segment.end);
    subtractInterval({ start: top, end: bottom }, leftCutters).forEach((segment) => {
      edgeSegments.push({ orientation: "vertical", x: left, y: segment.start, length: segment.end - segment.start });
    });

    const rightCutters = parts
      .filter((other) => other !== part && other.x === right)
      .map((other) => ({
        start: Math.max(top, other.y),
        end: Math.min(bottom, other.y + other.height),
      }))
      .filter((segment) => segment.start < segment.end);
    subtractInterval({ start: top, end: bottom }, rightCutters).forEach((segment) => {
      edgeSegments.push({ orientation: "vertical", x: right, y: segment.start, length: segment.end - segment.start });
    });
  });

  return edgeSegments;
};

const getCustomPartLabelStyle = ({ width, height, boothNumber, sizeText }) => {
  const padding = Math.max(1, Math.min(4, Math.min(width, height) * 0.12));
  const availableWidth = Math.max(1, width - padding * 2);
  const availableHeight = Math.max(1, height - padding * 2);
  const numberLength = Math.max(1, String(boothNumber || "").length);
  const sizeLength = Math.max(1, String(sizeText || "").length);
  const gap = Math.min(1.5, availableHeight * 0.08);

  let numberFontSize = Math.min(
    availableWidth / (numberLength * 0.72),
    availableHeight * 0.48,
    11,
  );
  let sizeFontSize = Math.min(
    availableWidth / (sizeLength * 0.62),
    availableHeight * 0.34,
    8,
  );

  const totalHeight = numberFontSize + sizeFontSize + gap;
  if (totalHeight > availableHeight) {
    const scale = availableHeight / totalHeight;
    numberFontSize *= scale;
    sizeFontSize *= scale;
  }

  const smallestFont = Math.min(numberFontSize, sizeFontSize);

  return {
    padding,
    gap,
    numberFontSize: Math.max(1, Math.floor(numberFontSize * 10) / 10),
    sizeFontSize: Math.max(1, Math.floor(sizeFontSize * 10) / 10),
    numberFontWeight: smallestFont < 4 ? 500 : smallestFont < 6 ? 600 : 700,
  };
};

export const BoothNode = memo(({ data, selected, id, width, height }) => {
  const { getNode, getNodes } = useReactFlow();
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  const [adjacentBooths, setAdjacentBooths] = useState({
    top: false,
    right: false,
    bottom: false,
    left: false,
  });
  const nodeData = data;
  // Get position from React Flow node and update when node moves
  useEffect(() => {
    const updatePosition = () => {
      const node = getNode(id);
      if (node) {
        setNodePosition(node.position);
      }
    };
    updatePosition();
    const interval = setInterval(updatePosition, 100);
    return () => clearInterval(interval);
  }, [id, getNode]);
  // Provide default values to prevent undefined errors
  const dimensions = React.useMemo(() => nodeData.dimensions || { length: 10, breadth: 10 }, [nodeData.dimensions]);
  const boothNumber = nodeData.number || "B1";
  const status = nodeData.status || "available";
  const companyLogo = nodeData.company_logo || nodeData.logo;
  // Use shared scale factor
  const nodeWidth = width || Math.max(dimensions.length * SCALE_FACTOR, 10); // Reduced minimum 10px width
  const nodeHeight = height || Math.max(dimensions.breadth * SCALE_FACTOR, 10); // Reduced minimum 10px height
  const boothShape = React.useMemo(
    () => normalizeBoothShape(nodeData.shape_json, dimensions),
    [nodeData.shape_json, dimensions],
  );
  const isCustomShape = boothShape.parts.length > 1;
  const customShapeBorderSegments = React.useMemo(
    () => (isCustomShape ? getExternalBorderSegments(boothShape.parts) : []),
    [boothShape.parts, isCustomShape],
  );

  // Calculate best solid box and booth number position for custom shapes (L, T, U, etc.)
  const bestSolidBox = React.useMemo(() => {
    if (!isCustomShape || !boothShape.parts || boothShape.parts.length <= 1) {
      return { x: 0, y: 0, width: nodeWidth, height: nodeHeight };
    }

    const parts = boothShape.parts;

    // 1. Gather all unique X and Y boundaries from parts
    const xSet = new Set();
    const ySet = new Set();
    parts.forEach((p) => {
      xSet.add(p.x);
      xSet.add(p.x + p.width);
      ySet.add(p.y);
      ySet.add(p.y + p.height);
    });

    const xCoords = Array.from(xSet).sort((a, b) => a - b);
    const yCoords = Array.from(ySet).sort((a, b) => a - b);

    const numCols = xCoords.length - 1;
    const numRows = yCoords.length - 1;

    if (numCols <= 0 || numRows <= 0) {
      return { x: 0, y: 0, width: nodeWidth, height: nodeHeight };
    }

    // 2. Build 2D boolean grid indicating solid cells
    const isSolidGrid = Array.from({ length: numRows }, () =>
      Array(numCols).fill(false)
    );

    for (let r = 0; r < numRows; r++) {
      const midY = (yCoords[r] + yCoords[r + 1]) / 2;
      for (let c = 0; c < numCols; c++) {
        const midX = (xCoords[c] + xCoords[c + 1]) / 2;
        const inside = parts.some(
          (p) =>
            midX >= p.x &&
            midX <= p.x + p.width &&
            midY >= p.y &&
            midY <= p.y + p.height
        );
        isSolidGrid[r][c] = inside;
      }
    }

    // 3. Find all maximal contiguous solid sub-rectangles in grid
    const candidates = [];

    for (let r1 = 0; r1 < numRows; r1++) {
      for (let r2 = r1; r2 < numRows; r2++) {
        for (let c1 = 0; c1 < numCols; c1++) {
          for (let c2 = c1; c2 < numCols; c2++) {
            let allSolid = true;
            for (let r = r1; r <= r2; r++) {
              for (let c = c1; c <= c2; c++) {
                if (!isSolidGrid[r][c]) {
                  allSolid = false;
                  break;
                }
              }
              if (!allSolid) break;
            }

            if (allSolid) {
              const xFt = xCoords[c1];
              const yFt = yCoords[r1];
              const wFt = xCoords[c2 + 1] - xCoords[c1];
              const hFt = yCoords[r2 + 1] - yCoords[r1];
              candidates.push({
                x: xFt * SCALE_FACTOR,
                y: yFt * SCALE_FACTOR,
                width: wFt * SCALE_FACTOR,
                height: hFt * SCALE_FACTOR,
                areaFt: wFt * hFt,
                wFt,
                hFt,
              });
            }
          }
        }
      }
    }

    if (candidates.length === 0) {
      return { x: 0, y: 0, width: nodeWidth, height: nodeHeight };
    }

    // 4. Score candidates: favor area, aspect ratio, and wider horizontal blocks for text
    let best = candidates[0];
    let maxScore = -1;

    candidates.forEach((cand) => {
      const aspect = Math.min(cand.wFt / cand.hFt, cand.hFt / cand.wFt);
      const widthBonus = cand.wFt >= cand.hFt ? 1.25 : 0.9;
      const score = cand.areaFt * (0.5 + 0.5 * aspect) * widthBonus;
      if (score > maxScore) {
        maxScore = score;
        best = cand;
      }
    });

    return best;
  }, [isCustomShape, boothShape.parts, nodeWidth, nodeHeight]);

  const boothNumberPosition = React.useMemo(() => {
    if (!isCustomShape || !boothShape.parts || boothShape.parts.length === 0) {
      return { left: 4, top: 4 };
    }
    // Find the top-left most part
    let topPart = boothShape.parts[0];
    boothShape.parts.forEach((p) => {
      if (p.y < topPart.y || (p.y === topPart.y && p.x < topPart.x)) {
        topPart = p;
      }
    });
    return {
      left: topPart.x * SCALE_FACTOR + 4,
      top: topPart.y * SCALE_FACTOR + 4,
    };
  }, [isCustomShape, boothShape.parts]);

  // Check for adjacent booths in all directions (accounting for rotation)
  useEffect(() => {
    const checkAdjacentBooths = () => {
      const allNodes = getNodes();
      const tolerance = 25; // Tolerance for position matching
      const rotation = nodeData.rotation || 0;
      const rotationRad = (rotation * Math.PI) / 180;
      const adjacent = {
        top: false,
        right: false,
        bottom: false,
        left: false,
      };
      // Current booth center
      const myCenterX = nodePosition.x + nodeWidth / 2;
      const myCenterY = nodePosition.y + nodeHeight / 2;
      allNodes.forEach((node) => {
        if (node.id === id || node.type !== "booth") return;
        const otherWidth = node.width || 80;
        const otherHeight = node.height || 80;
        const otherCenterX = node.position.x + otherWidth / 2;
        const otherCenterY = node.position.y + otherHeight / 2;
        // Vector from my center to other center
        const dx = otherCenterX - myCenterX;
        const dy = otherCenterY - myCenterY;
        // Rotate this vector to local coordinates (inverse rotation)
        const localDx =
          dx * Math.cos(-rotationRad) - dy * Math.sin(-rotationRad);
        const localDy =
          dx * Math.sin(-rotationRad) + dy * Math.cos(-rotationRad);
        // Distance to other booth center in local coordinates
        const distX = Math.abs(localDx);
        const distY = Math.abs(localDy);
        // Expected distance if adjacent
        const expectedDistX = (nodeWidth + otherWidth) / 2;
        const expectedDistY = (nodeHeight + otherHeight) / 2;
        // Check if booth is adjacent in each local direction
        // TOP: other booth is above in local space
        if (
          localDy < 0 &&
          Math.abs(distY - expectedDistY) <= tolerance &&
          distX < expectedDistX
        ) {
          adjacent.top = true;
        }
        // BOTTOM: other booth is below in local space
        if (
          localDy > 0 &&
          Math.abs(distY - expectedDistY) <= tolerance &&
          distX < expectedDistX
        ) {
          adjacent.bottom = true;
        }
        // LEFT: other booth is to the left in local space
        if (
          localDx < 0 &&
          Math.abs(distX - expectedDistX) <= tolerance &&
          distY < expectedDistY
        ) {
          adjacent.left = true;
        }
        // RIGHT: other booth is to the right in local space
        if (
          localDx > 0 &&
          Math.abs(distX - expectedDistX) <= tolerance &&
          distY < expectedDistY
        ) {
          adjacent.right = true;
        }
      });
      setAdjacentBooths(adjacent);
    };
    checkAdjacentBooths();
    // Re-check when nodes might have changed
    const interval = setInterval(checkAdjacentBooths, 200);
    return () => clearInterval(interval);
  }, [id, nodePosition, nodeWidth, nodeHeight, getNodes, nodeData.rotation]);

  // Calculate font size based on booth size and label length.
  const minDimension = Math.min(nodeWidth, nodeHeight);
  const boothSizeText = `${dimensions.length} X ${dimensions.breadth}`;
  const calculatedFontSize = getAdaptiveFontSize({
    width: nodeWidth,
    height: nodeHeight,
    text: `${boothNumber}\n${boothSizeText}`,
    base: Math.min(16, minDimension / 6),
    min: 4,
    max: 16,
    padding: 3,
    lineHeight: 1.15,
  });

  // Handle resize to update dimensions in feet
  const handleResize = useCallback(
    (newWidth, newHeight, newPosition) => {
      const MAX_DIMENSION_FT = 10000;
      let newLength = Math.max(1, Math.round(newWidth / SCALE_FACTOR));
      let newBreadth = Math.max(1, Math.round(newHeight / SCALE_FACTOR));
      newLength = Math.min(newLength, MAX_DIMENSION_FT);
      newBreadth = Math.min(newBreadth, MAX_DIMENSION_FT);
      const resizeEvent = new CustomEvent("nodeResize", {
        detail: {
          nodeId: id,
          nodeType: "booth",
          width: Math.round(newWidth),
          height: Math.round(newHeight),
          dimensions: { length: newLength, breadth: newBreadth },
          position: newPosition,
        },
      });
      window.dispatchEvent(resizeEvent);
    },
    [id] // scaleFactor removed from dependency as it's now SCALE_FACTOR from utils
  );

  const handleRotate = useCallback(
    (newRotation) => {
      const rotateEvent = new CustomEvent("nodeRotate", {
        detail: {
          nodeId: id,
          nodeType: "booth",
          rotation: newRotation,
        },
      });
      window.dispatchEvent(rotateEvent);
    },
    [id],
  );

  // Handle adding adjacent booth
  const handleAddAdjacentBooth = useCallback(
    (direction) => {
      const rotation = nodeData.rotation || 0;
      const addBoothEvent = new CustomEvent("addAdjacentBooth", {
        detail: {
          sourceNodeId: id,
          sourceNodeData: nodeData,
          sourcePosition: nodePosition,
          sourceWidth: nodeWidth,
          sourceHeight: nodeHeight,
          direction: direction,
          dimensions: dimensions,
          rotation: rotation, // Pass rotation so FloorCanvas can calculate correct position
        },
      });
      window.dispatchEvent(addBoothEvent);
    },
    [id, nodeData, nodePosition, nodeWidth, nodeHeight, dimensions],
  );

  // Apply shadow settings
  const shadowStyle = nodeData.shadowSettings
    ? {
        boxShadow: `${nodeData.shadowSettings.offsetX}px ${nodeData.shadowSettings.offsetY}px ${nodeData.shadowSettings.blur}px ${nodeData.shadowSettings.color}`,
      }
    : {};

  // Get and apply event settings for colors from the Zustand store
  const eventSettings = useFloorPlanner((state) => state.eventSettings);
  
  const getBackgroundColor = () => {
    if (nodeData.isHighlighted) return undefined;
    
    // If a specific background color was manually set, prioritize it
    // unless it's the default, in which case use status-based colors
    if (nodeData.backgroundColor && nodeData.backgroundColor !== "#f5f5f5") {
      return nodeData.backgroundColor;
    }
    
    switch (status) {
      case "available":
        return eventSettings.booth_color_available || "#ffffff";
      case "assigned":
        return eventSettings.booth_color_assigned || "#ff69b4";
      case "onHold":
      case "reserved":
        return eventSettings.booth_color_reserved || "#d3d3d3";
      default:
        return eventSettings.booth_color_available || "#ffffff";
    }
  };

  const backgroundColor = getBackgroundColor();
  const textColor = nodeData.textColor || "#374151";
  const fontFamily = nodeData.fontFamily || "Inter";
  const rotation = nodeData.rotation || 0;
  // Check if node is locked
  const isLocked = nodeData.locked || false;

  return (
    <div
      className="relative w-full h-full"
      data-id={id}
      style={{ overflow: "visible" }}
    >
      {/* NodeResizer/SelectionOverlay must be direct child of node for React Flow to work properly */}
      {!isLocked && selected && (
        <SelectionOverlay
          selected={selected || false}
          onRotate={handleRotate}
          onResize={handleResize}
          minWidth={60}
          minHeight={60}
          nodeId={id}
          width={nodeWidth}
          height={nodeHeight}
          rotation={rotation}
          nodeX={nodePosition.x}
          nodeY={nodePosition.y}
        />
      )}

      {/* Directional Triangle Arrows - Zoho Backstage style - inside rotating container */}
      {!isLocked && selected && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: rotation ? `rotate(${rotation}deg)` : undefined,
            transformOrigin: "center center",
            overflow: "visible",
            zIndex: 10000,
          }}
        >
          {/* Top Arrow */}
          {!adjacentBooths.top && (
            <TriangleArrow
              direction="top"
              onClick={() => handleAddAdjacentBooth("top")}
            />
          )}

          {/* Right Arrow */}
          {!adjacentBooths.right && (
            <TriangleArrow
              direction="right"
              onClick={() => handleAddAdjacentBooth("right")}
            />
          )}

          {/* Bottom Arrow */}
          {!adjacentBooths.bottom && (
            <TriangleArrow
              direction="bottom"
              onClick={() => handleAddAdjacentBooth("bottom")}
            />
          )}

          {/* Left Arrow */}
          {!adjacentBooths.left && (
            <TriangleArrow
              direction="left"
              onClick={() => handleAddAdjacentBooth("left")}
            />
          )}
        </div>
      )}

      <div
        className={`relative flex items-center justify-center w-full h-full ${
          nodeData.isHighlighted && !isCustomShape ? "booth-blinking" : ""
        }`}
        style={{
          transform: `rotate(${rotation}deg) scale(${nodeData.flippedH ? -1 : 1}, ${nodeData.flippedV ? -1 : 1})`,
          backgroundColor: isCustomShape ? "transparent" : nodeData.isHighlighted ? undefined : backgroundColor,
          color: textColor,
          fontFamily: fontFamily,
          borderRadius:
            isCustomShape
              ? 0
              : nodeData.cornerRadius !== undefined
              ? `${nodeData.cornerRadius}px`
              : "8px",
          border: isCustomShape ? "0" : "1px solid #d1d5db",
          transition: "box-shadow 0.2s ease",
          cursor: status === "available" || !status ? "pointer" : "default",
          ...(isCustomShape ? {} : shadowStyle),
        }}
      >
        {isCustomShape &&
          boothShape.parts.map((part) => (
            <div
              key={part.id}
              className={`absolute ${nodeData.isHighlighted ? "booth-blinking" : ""}`}
              style={{
                left: part.x * SCALE_FACTOR,
                top: part.y * SCALE_FACTOR,
                width: part.width * SCALE_FACTOR,
                height: part.height * SCALE_FACTOR,
                backgroundColor: nodeData.isHighlighted ? undefined : backgroundColor,
                border: "0",
                borderRadius: 0,
                boxSizing: "border-box",
                ...shadowStyle,
              }}
            />
          ))}
        {isCustomShape &&
          customShapeBorderSegments.map((segment, index) => (
            <div
              key={`${segment.orientation}-${index}`}
              className="absolute"
              style={{
                left: segment.x * SCALE_FACTOR,
                top: segment.y * SCALE_FACTOR,
                width:
                  segment.orientation === "horizontal"
                    ? segment.length * SCALE_FACTOR
                    : 1,
                height:
                  segment.orientation === "vertical"
                    ? segment.length * SCALE_FACTOR
                    : 1,
                backgroundColor: "#d1d5db",
                zIndex: 2,
              }}
            />
          ))}
        {isCustomShape &&
          status === "available" &&
          boothShape.parts.map((part) => {
            const partWidth = part.width * SCALE_FACTOR;
            const partHeight = part.height * SCALE_FACTOR;
            const partSizeText = `${part.width} X ${part.height}`;
            const labelStyle = getCustomPartLabelStyle({
              width: partWidth,
              height: partHeight,
              boothNumber,
              sizeText: partSizeText,
            });
            const flipX = nodeData.flippedH ? -1 : 1;
            const flipY = nodeData.flippedV ? -1 : 1;

            return (
              <div
                key={`label-${part.id}`}
                className="absolute flex flex-col items-center justify-center text-center pointer-events-none overflow-hidden"
                style={{
                  left: part.x * SCALE_FACTOR,
                  top: part.y * SCALE_FACTOR,
                  width: partWidth,
                  height: partHeight,
                  color: textColor,
                  zIndex: 3,
                  transform: `scale(${flipX}, ${flipY}) rotate(${-rotation}deg)`,
                  transformOrigin: "center center",
                  padding: labelStyle.padding,
                  boxSizing: "border-box",
                  gap: labelStyle.gap,
                }}
              >
                <div
                  className="max-w-full truncate"
                  style={{
                    fontSize: `${labelStyle.numberFontSize}px`,
                    lineHeight: 1,
                    fontWeight: labelStyle.numberFontWeight,
                  }}
                >
                  {boothNumber}
                </div>
                <div
                  className="max-w-full truncate"
                  style={{
                    fontSize: `${labelStyle.sizeFontSize}px`,
                    lineHeight: 1,
                    opacity: 0.8,
                    fontWeight: "normal",
                  }}
                >
                  {partSizeText}
                </div>
              </div>
            );
          })}
        <div
          className="relative w-full h-full select-none pointer-events-none"
          style={{
            color: textColor,
            fontFamily: fontFamily,
          }}
        >
          {/* AVAILABLE → only center number */}
          {status === "available" && !isCustomShape && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center font-bold overflow-hidden px-0.5"
              style={{
                fontSize: `${calculatedFontSize}px`,
                lineHeight: 1.1,
              }}
            >
              <div className="max-w-full truncate">{boothNumber}</div>
              <div
                className="max-w-full truncate"
                style={{
                  fontSize: `${Math.max(3.5, calculatedFontSize * 0.65)}px`,
                  opacity: 0.8,
                  marginTop: "1px",
                  fontWeight: "normal",
                }}
              >
                {boothSizeText}
              </div>
            </div>
          )}

          {/* ASSIGNED, RESERVED, or ONHOLD → number top-left + content inside bestSolidBox */}
          {(status === "assigned" || status === "reserved" || status === "onHold") && (
            <>
              {/* Booth number top-left of solid region */}
              <div
                className="absolute font-bold pointer-events-none"
                style={{
                  left: `${boothNumberPosition.left}px`,
                  top: `${boothNumberPosition.top}px`,
                  fontSize: `${Math.max(3.5, calculatedFontSize * 0.7)}px`,
                  opacity: 0.8,
                  lineHeight: 1,
                  maxWidth: "70%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  zIndex: 5,
                }}
              >
                {boothNumber}
              </div>

              {/* Center Content: Logo or Name inside bestSolidBox */}
              <div
                className="absolute flex items-center justify-center text-center px-1 overflow-hidden pointer-events-none"
                style={{
                  left: `${bestSolidBox.x}px`,
                  top: `${bestSolidBox.y}px`,
                  width: `${bestSolidBox.width}px`,
                  height: `${bestSolidBox.height}px`,
                  zIndex: 4,
                }}
              >
                {eventSettings.booth_label_type === "company_logo" && companyLogo ? (
                  <img 
                    src={companyLogo} 
                    alt={nodeData.company_name}
                    className="max-w-[90%] max-h-[90%] object-contain"
                  />
                ) : (
                  <div
                    className="w-full break-words font-semibold px-1"
                    style={{
                      fontSize: `${Math.max(3.5, getAdaptiveFontSize({
                        width: bestSolidBox.width,
                        height: bestSolidBox.height,
                        text: nodeData.company_name || (status === "reserved" ? "Reserved" : ""),
                        base: calculatedFontSize * 0.85,
                        min: 3.5,
                        max: 14,
                        padding: 6,
                      }))}px`,
                      lineHeight: 1.1,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                    title={nodeData.company_name}
                  >
                    {nodeData.company_name || (status === "reserved" ? "Reserved" : "")}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {/* Connection handles - hidden */}
        <Handle
          type="target"
          position={Position.Top}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />

        <Handle
          type="target"
          position={Position.Left}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />

        <Handle
          type="source"
          position={Position.Right}
          className="w-2 h-2 bg-background border-border opacity-0 hover:opacity-100"
        />
      </div>
    </div>
  );
});
